import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { seedState, initials } from './data.js'
import { markNotificationRead, markAllNotificationsRead } from './lib/api'

const AppContext = createContext(null)
export const useApp = () => useContext(AppContext)

const STORAGE_KEY = 'rfState_react'

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {}
  return seedState()
}

export function AppProvider({ children }) {
  const [state, setStateInternal] = useState(loadState)
  // Restore last visited page from localStorage so refresh doesn't reset to dashboard.
  // Falls back to 'dashboard' if nothing is stored.
  // Safe: Sidebar's isPageAllowed() guard will redirect to role default if stored page is not allowed.
  const [page, setPage] = useState(() => localStorage.getItem('currentPage') || 'dashboard')
  const [toasts, setToasts] = useState([])
  const [modal, setModal] = useState(null)
  const [notifOpen, setNotifOpen] = useState(false)
  // Real, server-backed notification feed (separate from the persisted mock
  // `state.notifs`). Populated by NotificationsPoller; consumed by the bell +
  // drawer. Kept out of the localStorage-persisted `state` on purpose.
  const [notifications, setNotificationsState] = useState([])
  const [browserNotifyEnabled, setBrowserNotifyEnabledState] = useState(() => {
    try {
      const v = localStorage.getItem('browserNotifyEnabled')
      return v === null ? true : v === 'true'
    } catch { return true }
  })
  const [navDrawerOpen, setNavDrawerOpen] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [pageContext, setPageContext] = useState(null)
  const toastIdRef = useRef(0)

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)) } catch {}
  }, [state])

  useEffect(() => {
    document.documentElement.dataset.theme = state.settings.theme || 'dark'
  }, [state.settings.theme])

  useEffect(() => {
    document.documentElement.dataset.fontSize = state.settings.fontSize || 'default'
  }, [state.settings.fontSize])

  useEffect(() => {
    document.documentElement.dataset.density = state.settings.density || 'default'
  }, [state.settings.density])

  const updateState = useCallback((updater) => {
    setStateInternal(prev => typeof updater === 'function' ? updater(prev) : { ...prev, ...updater })
  }, [])

  const dismissToast = useCallback((id) => {
    setToasts(t => t.filter(x => x.id !== id))
  }, [])

  const toast = useCallback((msg, type = 'success') => {
    const id = ++toastIdRef.current
    setToasts(t => [...t, { id, msg, type }])
    const duration = type === 'error' ? 5000 : 3000
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), duration)
  }, [])

  const openModal = useCallback((content, size = '') => setModal({ content, size }), [])
  const closeModal = useCallback(() => setModal(null), [])

  const toggleTheme = useCallback(() => {
    setStateInternal(prev => ({ ...prev, settings: { ...prev.settings, theme: prev.settings.theme === 'light' ? 'dark' : 'light' } }))
  }, [])

  const setSetting = useCallback((key, value) => {
    setStateInternal(prev => ({ ...prev, settings: { ...prev.settings, [key]: value } }))
  }, [])

  const goPage = useCallback((p, payload = null) => {
    setPage(p)
    localStorage.setItem('currentPage', p) // persist so refresh restores this page
    setPageContext(payload)
    setSidebarOpen(false)
    window.scrollTo(0, 0)
  }, [])

  const clearPageContext = useCallback(() => setPageContext(null), [])

  // ─── Notifications feed ──────────────────────────────────────
  const setNotifications = useCallback((updater) => {
    setNotificationsState(prev => typeof updater === 'function' ? updater(prev) : updater)
  }, [])

  const markNotifRead = useCallback(async (id) => {
    // Optimistic — flip locally, then persist (ignore network failures).
    setNotificationsState(prev => prev.map(n => n.id === id
      ? { ...n, readAt: n.readAt || new Date().toISOString() }
      : n))
    try { await markNotificationRead(id) } catch {}
  }, [])

  const markAllNotifsRead = useCallback(async () => {
    setNotificationsState(prev => prev.map(n => n.readAt
      ? n
      : { ...n, readAt: new Date().toISOString() }))
    try { await markAllNotificationsRead() } catch {}
  }, [])

  const setBrowserNotify = useCallback((v) => {
    setBrowserNotifyEnabledState(!!v)
    try { localStorage.setItem('browserNotifyEnabled', String(!!v)) } catch {}
  }, [])

  const addReport = (rep) => updateState(s => ({ ...s, reports: [rep, ...s.reports] }))
  const deleteReport = (id) => updateState(s => ({ ...s, reports: s.reports.filter(r => r.id !== id) }))
  const addEmployee = (e) => updateState(s => ({ ...s, employees: [...s.employees, e] }))
  const updateEmployee = (id, patch) => updateState(s => ({ ...s, employees: s.employees.map(e => e.id === id ? { ...e, ...patch } : e) }))
  const deleteEmployee = (id) => updateState(s => ({ ...s, employees: s.employees.filter(e => e.id !== id) }))
  const updateUser = (patch) => updateState(s => ({ ...s, user: { ...s.user, ...patch, initials: patch.name ? initials(patch.name) : s.user.initials } }))
  const updateSettings = (patch) => updateState(s => ({ ...s, settings: { ...s.settings, ...patch } }))
  const updateNotifs = (updater) => updateState(s => ({ ...s, notifs: typeof updater === 'function' ? updater(s.notifs) : updater }))

  const value = {
    state, updateState, toast, dismissToast, modal, openModal, closeModal,
    page, goPage, pageContext, clearPageContext, toggleTheme, setSetting,
    notifOpen, setNotifOpen, navDrawerOpen, setNavDrawerOpen, sidebarOpen, setSidebarOpen,
    notifications, setNotifications, markNotifRead, markAllNotifsRead,
    browserNotifyEnabled, setBrowserNotify,
    toasts,
    addReport, deleteReport, addEmployee, updateEmployee, deleteEmployee,
    updateUser, updateSettings, updateNotifs
  }
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}
